// src/pos-app/stations/WaiterStation.js
//
// F12a Task 8.1 — the native Aqana POS app's waiter (table service) screen.
// Sibling of CashierStation (Phase 7): same shell composition (AuroraBackground
// calm → StationTopBar → tab panes → BottomTabs; visual reference
// docs/mockups/f12a/waiter-station.html) around the EXISTING, fully-working
// TableServicePage — reused as-is, not rewritten.
//
// Why TableServicePage (not WaiterSalesPage): the web FunctionsPage's
// "Table Service" tile launches /functions/table-service, i.e. TableServicePage
// is the waiter surface tenants actually use today (WaiterSalesPage's route
// exists in App.js but no hub tile points at it). It is also explicitly
// excluded from the Electron POS (host.isPosDevice filter in FunctionsPage) —
// it IS the phone/tablet waiter surface, which is exactly this station.
//
// Tabs (two, not the mockup's three):
//   Tables — the real <TableServicePage/> (floor plan, reservations, waitlist,
//            and the full order flow via its internal WaiterOrderDrawer). Kept
//            MOUNTED across tab switches and mid-shift locks (everUnlockedRef)
//            so an in-progress table/order survives a peek at Shift or an
//            operator handover.
//   Shift  — operator card + End shift → lock(). Mirrors CashierStation's pane
//            (small enough that duplication beats a premature extraction).
// The mockup's Order tab is OMITTED: table→order is internal to
// TableServicePage (drawer over the floor plan, driven by its own state), so
// "a table is open" is not knowable from out here without modifying the page.
// Per the brief, Tables is the single surface rather than a faked/dimmed tab.
//
// Operator session: useOperatorSession — boots locked; unlock mirrors the web
// FunctionsPage employee-auth flow (setAuthenticatedState + preferred
// language) so the reused page attributes orders to the right employee.
//
// Web-navigation escapes: TableServicePage internally calls
// navigate('/functions') (header back arrow) and
// navigate('/functions/cashier-sales') (its switch-to-cashier PIN flow); its
// children (VisualTableSelector, WaiterOrderDrawer) never navigate — the
// table→order flow is pure component state, so there is no internal routing
// to preserve. Same interception as CashierStation: ANY departure from the
// mount pathname is reverted (replace:true), and /functions/lock — not used
// by this page today, but kept for uniformity — triggers the shell lock().
// Zero changes to the shared 1,800-line page.
//
// Orientation: NOT LOCKED (2026-08-27). This station used to lock phones to
// portrait, with an isRealIPad() carve-out to stop an iPad mini being
// misclassified as a phone by the 768px breakpoint. All of it is gone.
//
// Two independent native-layout failures came from that one call:
//   1. On iPadOS 26, a native lock could force the app into a
//      floating/windowed state where WKWebView hit-testing desyncs from what
//      it paints — taps silently land on nothing on a perfectly healthy DOM.
//      This is what broke the PIN pad, and then the whole waiter tree, in the
//      field. CashierStation removed its own lock for this reason (f8548be).
//   2. A lock re-lays out the WebView and destroys the content inset that
//      capacitor.config.ts's ios.contentInset='always' provides, putting the
//      station chrome back under the Dynamic Island — the exact defect F15
//      part 2 fixed. Measured directly on WaiterSalesStation, which was built
//      with a copy of this lock, reproduced the overlap, and lost it as soon
//      as the lock was removed.
//
// The unconditional setOrientation(null) sweep REMAINS and now matters more:
// a device enrolled here after being a KDS station (KdsStation.js locks
// 'landscape' on mount) carries that stale lock in, and never setting a new
// lock does not clear one already applied.
//
// Surface flag (F12a-N): SURFACES.waiter in surfaces/config.js picks what
// the Tables tab hosts — 'native' mounts the purpose-built <TablesSurface/>
// (surfaces/waiter/), 'legacy' keeps the wrapped TableServicePage plus the
// navigation-escape interception below. Mirrors CashierStation's switch: the
// interception effect lives in <LegacyNavigationGuard/> and is simply not
// registered in native mode (TablesSurface never navigates), while the
// business-day gate below stays ABOVE both surfaces. Flipping back to
// 'legacy' fully restores the wrapper behavior.
//
// Cashier switch entry point is the Sales bottom tab (see `tabs` below); the
// top bar carries only CashierTopBarExtras while salesMode is on.
//
// W5 REVIEW FIX (CRITICAL finding 2 — SellSurface was half-functional in
// sales-mode): this used to mount a bare `<SellSurface onLock={lock}/>` with
// none of CashierStation's surrounding composition. SellSurface is NOT
// self-sufficient on its own — it PUBLISHES its session/orders/printing
// state and a callback ref into a context bridge (TopBarExtras.js's
// `SellExtrasProvider`/`usePublishSellExtras`) rather than rendering that
// chrome itself, and it drives an "Orders" pane via an `ordersTabActive`
// prop + `onRequestOrdersTab`/`onRequestSellTab` callbacks rather than
// owning a tab of its own. Without a `<SellExtrasProvider>` ancestor and
// cashier's `<TopBarExtras/>` consuming it, that snapshot has no reader:
// the session pill and More menu (Staff Allowance / printing toggles /
// Receipt Translations / — critically — End Session) simply don't exist,
// so `showEoD` can never flip true and the REUSED `<EndOfDayFlow/>` SellSurface
// renders internally on End Session is unreachable dead code in this
// context. And without `ordersTabActive`/`onRequestOrdersTab` wired to
// something, `onRequestOrdersTab()` (fired by an occupied-table tap or the
// bill-request banner's "View order") and the embedded `OrdersPanelNative`'s
// close handler are no-ops — tapping an occupied table in sales-mode did
// nothing.
//
// Fix: replicate CashierStation's MINIMAL composition around SellSurface —
// `<SellExtrasProvider>` wraps this station's whole unlocked frame (inert,
// zero DOM/behavior change, while salesMode is off — nothing publishes to
// it), the REUSED cashier `<TopBarExtras/>` mounts in StationTopBar's
// children slot while salesMode is on, and `salesOrdersOpen` (below) is a local
// in-memory flag standing in for CashierStation's bottom Orders TAB — same
// role, `ordersTabActive`/`onRequestOrdersTab`/`onRequestSellTab`, just no
// visible tab button (this station has no Orders tab at all, native or
// legacy) since SellSurface's own embedded panel is reachable purely via
// the table-tap/bill-banner/close-callback wiring, no separate nav needed.
// `operatorUnlockedAt={shiftStartedAt}` is the SAME state this station
// already tracks for its own Shift pane, reused verbatim (mirrors
// CashierStation's own re-use of its `shiftStartedAt`).
//
// EOD -> lock exiting sales-mode falls out of EXISTING machinery, no new
// code needed: SellSurface's `handleSessionClosed` (now reachable via the
// End Session menu item) calls `AuthContext.logout()`; the "lock on logout"
// effect below already locks this station whenever `isAuthenticated` drops
// while an operator is signed in; and the "lock always exits sales-mode"
// effect already resets `salesMode`/`salesPinOpen` (now also
// `salesOrdersOpen`) whenever `locked` flips true. Same chain CashierStation
// relies on for the identical flow.
//
// Extraction decision (task brief's explicit ask): did NOT pull this
// composition into a shared `CashierSalesHost` used by both stations.
// CashierStation.js/.test.js is ~980 lines combined with its own tab
// machinery (activeTab/BottomTabs/OrdersTabBadge) tightly interleaved with
// this exact composition — extracting a component clean enough to not
// touch that file's render path would still require re-threading its
// StationTopBar children slot and Sell/Orders pane, i.e. a real refactor of
// the ONE file explicitly called out as this task's highest regression
// risk, for a ~10-line payoff. Mirrored minimally instead, with this
// comment as the drift warning: if CashierStation's composition around
// SellSurface changes (new props, new bridge fields, new gate ordering),
// this block must be updated to match by hand — nothing enforces the two
// staying in sync. Zero edits to CashierStation.js/.test.js either way.
//
// Escape-chain centralization (Task W5, item 2): the Sales PIN modal's
// OPEN state lives here (it must outlive TablesSurface unmounting when
// salesMode flips true), but its Escape-key handling is ranked inside
// TablesSurface's single keydown authority — `salesPinOpen`/
// `onCloseSalesPin` are threaded down as plain props, same pattern as
// `locked` already uses. See TablesSurface.js's header for the full chain.
//
// Business-day gate (F12a redirect-loop fix): TableServicePage carries
// useBusinessDayGuard (src/hooks/pos/useBusinessDayGuard.js), which
// navigate('/functions')s whenever there is no active business day —
// isBusinessDay defaults to false, so this fires routinely outside opening
// hours. The escape-interception effect below then reverted that navigate
// straight back onto TableServicePage, which re-fired the guard: an infinite
// loop. useBusinessDayGate() reads the same state and, while closed, this
// station simply never mounts TableServicePage in the Tables pane — it shows
// DayClosedScreen instead, so the guard has nothing to fire from. The Shift
// tab (and End shift) stay available regardless, so an operator can still
// hand over / lock out if the business day closes mid-shift.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AuroraBackground, { glass } from '../shell/AuroraBackground';
import StationTopBar from '../shell/StationTopBar';
import BottomTabs from '../shell/BottomTabs';
import useOperatorSession from '../shell/useOperatorSession';
import useLockSessionStatus from '../../hooks/pos/useLockSessionStatus';
import StationSettingsSheet from '../StationSettingsSheet';
import OperatorLockScreen from '../OperatorLockScreen';
import { ConnectionBanner } from '../ConnectionStatus';
import DayClosedScreen from '../shell/DayClosedScreen';
import useBusinessDayGate from '../shell/useBusinessDayGate';
import TableServicePage from '../../pages/pos/TableServicePage';
import { SURFACES } from '../surfaces/config';
import TablesSurface from '../surfaces/waiter/TablesSurface';
import SalesPinModal from '../surfaces/waiter/SalesPinModal';
import SellSurface from '../surfaces/cashier/SellSurface';
import CashierTopBarExtras, { SellExtrasProvider } from '../surfaces/cashier/TopBarExtras';
import { setOrientation } from '../../platform/nativeChrome';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { useStation } from '../../station/StationContext';

// PHONE_MAX_DIM and isRealIPad() are gone (2026-08-27): both existed only to
// decide WHICH devices got a portrait lock, and this station no longer locks
// orientation on any device. See the orientation effect for why.

const CSS = `
/* Frame padding moved off the FRAME and onto the body (F15 part 2). The top
   bar and the bottom tab bar are chrome: they must reach the display edges
   like native bars, with content passing under them. Only the content column
   between them is inset. */
.wst-frame{flex:1;min-height:0;display:flex;flex-direction:column;box-sizing:border-box;}
.wst-body{flex:1;min-height:0;display:flex;padding:10px 12px 0;box-sizing:border-box;}
.wst-pane{flex:1;min-width:0;min-height:0;display:flex;flex-direction:column;}

/* Tables — the real TableServicePage inside a glass-edged, clipped viewport. */
.wst-tables{flex:1;min-height:0;position:relative;overflow:hidden;}
.wst-tables-inner{position:absolute;inset:0;display:flex;flex-direction:column;background:var(--c-bg);}
/* Native TablesSurface fills the same clipped slot but keeps the glass visible. */
.wst-tables-native{position:absolute;inset:0;display:flex;flex-direction:column;overflow:hidden;}

/* Shift — operator card + end shift (mirrors CashierStation's pane). */
.wst-shift{flex:1;min-height:0;overflow-y:auto;display:flex;align-items:center;justify-content:center;
  padding:24px;box-sizing:border-box;}
.wst-op-card{width:360px;max-width:100%;box-sizing:border-box;border-radius:20px;padding:26px 24px 22px;
  display:flex;flex-direction:column;align-items:center;gap:6px;text-align:center;}
.wst-op-avatar{width:68px;height:68px;border-radius:22px;margin-bottom:8px;flex:none;
  display:flex;align-items:center;justify-content:center;color:#fff;
  background:linear-gradient(135deg,var(--c-green),var(--c-teal));
  font-size:22px;font-weight:800;letter-spacing:.3px;
  box-shadow:0 6px 16px color-mix(in srgb, var(--c-green) 30%, transparent);}
.wst-op-name{font-size:18px;font-weight:800;letter-spacing:-.2px;color:var(--c-ink);}
.wst-op-role{font-size:11.5px;font-weight:800;text-transform:uppercase;letter-spacing:.8px;
  color:var(--c-green);background:var(--c-greenLight);border:1px solid var(--c-greenBorder);
  border-radius:999px;padding:4px 12px;margin:4px 0;}
.wst-op-since{font-size:12.5px;color:var(--c-muted);font-weight:600;
  display:flex;align-items:center;gap:6px;}
.wst-end{margin-top:18px;width:100%;min-height:56px;border:none;border-radius:14px;cursor:pointer;
  display:flex;align-items:center;justify-content:center;gap:9px;
  background:var(--c-red);color:#fff;font-size:15px;font-weight:800;font-family:inherit;
  box-shadow:0 5px 14px color-mix(in srgb, var(--c-red) 35%, transparent);
  transition:transform .15s ease, filter .15s ease;}
.wst-end:active{transform:scale(.96);filter:brightness(1.1);}
[dir="rtl"] .wst-end svg{transform:scaleX(-1);}
`;

const ClockIcon = (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
    </svg>
);

const EndShiftIcon = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
        <path d="m16 17 5-5-5-5M21 12H9" />
    </svg>
);

function initials(name) {
    return String(name || '')
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((word) => word.charAt(0))
        .join('')
        .toUpperCase();
}

// ---- neutralize TableServicePage's web-navigation escapes --------------
// Legacy surface only (see header note). Any navigate() away from the
// station's mount path is reverted; the /functions/lock route (unused by
// this page today, kept for shell uniformity) additionally triggers the
// shell lock. Some of these escapes (e.g. the switch-to-cashier PIN flow)
// mutate AuthContext's identity synchronously BEFORE navigating — reverting
// the navigation alone leaves that identity swap committed, silently
// misattributing subsequent orders to whoever was authenticated on the other
// end. So on every intercepted escape, while an operator is signed in here,
// re-assert this station's operator identity. Lives in its own child
// component (mirroring CashierStation's LegacyNavigationGuard) so the
// interception effect — and the useLocation subscription — simply is not
// registered when the native surface is active.
function LegacyNavigationGuard({ lock, operator, assertOperatorIdentity }) {
    const location = useLocation();
    const navigate = useNavigate();
    const homePathRef = useRef(location.pathname);
    useEffect(() => {
        const home = homePathRef.current;
        if (location.pathname === home) return;
        if (location.pathname === '/functions/lock') lock();
        if (operator != null) assertOperatorIdentity(operator);
        navigate(home, { replace: true });
    }, [location.pathname, lock, navigate, operator, assertOperatorIdentity]);
    return null;
}

export default function WaiterStation() {
    const { tenantId, isAuthenticated, setAuthenticatedState } = useAuth();
    const { t, changeLanguage } = useLanguage();
    const { config } = useStation();
    // Belt-and-braces: fall back to the tenantId persisted at enrollment
    // (station config) if AuthContext's ever comes back empty — see
    // stationConfig.js and useOperatorSession.js.
    const effectiveTenantId = tenantId || config?.tenantId || '';
    const { operator, locked, unlocking, error, lock, unlock } = useOperatorSession(config?.tenantId);
    const { dayOpen, loading: dayLoading, refresh: refreshDay } = useBusinessDayGate();
    // Session-status chip on the lock screen (task-n8) — same hook
    // CashierStation uses; see its header comment for the data-source
    // rationale (pragmatic store-level "any open session" reading, since
    // there is no signed-in operator to match per-employee while locked).
    const sessionStatus = useLockSessionStatus(locked);

    const [activeTab, setActiveTab] = useState('tables');
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [shiftStartedAt, setShiftStartedAt] = useState(null);

    // Cashier switch (Task W5, native mode only) — see header note.
    const [salesMode, setSalesMode] = useState(false);
    const [salesPinOpen, setSalesPinOpen] = useState(false);
    // W5 review fix (finding 2) — in-memory stand-in for CashierStation's
    // bottom Orders TAB: SellSurface expects `ordersTabActive` +
    // `onRequestOrdersTab`/`onRequestSellTab`, not a boolean it owns itself.
    // No visible tab button here (this station has no Orders tab, native or
    // legacy) — SellSurface's own table-tap / bill-banner / embedded-panel
    // close wiring is the only way this flips, exactly mirroring what a real
    // Orders tab would do from the outside.
    const [salesOrdersOpen, setSalesOrdersOpen] = useState(false);

    // Tables surface flavour — build-time constant (surfaces/config.js).
    const nativeTables = SURFACES.waiter === 'native';

    // The floor content mounts on first unlock and then STAYS mounted across
    // mid-shift locks so an in-progress table/order survives a handover.
    const everUnlockedRef = useRef(false);
    if (operator != null) everUnlockedRef.current = true;
    const everUnlocked = everUnlockedRef.current;

    // ---- orientation: never locked (2026-08-27) -------------------------
    // This used to lock phones to portrait. It no longer locks anything, and
    // the phone breakpoint check that drove it is gone with it.
    //
    // Why: a native orientation lock re-lays out the WKWebView, and the
    // content inset that capacitor.config.ts's ios.contentInset='always'
    // relies on does NOT survive it — the station chrome renders back under
    // the Dynamic Island, which is the exact defect F15 part 2 fixed. This was
    // measured directly: WaiterSalesStation was built with a copy of the lock
    // below, reproduced the overlap, and lost it the moment the lock was
    // removed, on an otherwise identical build.
    //
    // CashierStation had already dropped its own lock (f8548be, 2026-07-18)
    // because a native lock on iPadOS 26 could force the app into a
    // floating/windowed state where hit-testing desyncs from what is painted —
    // taps landing on nothing on a perfectly healthy DOM. That is a second,
    // independent native-layout failure from the same call. All three POS
    // stations now agree: do not lock.
    //
    // The product requirement this served (waiters hold phones portrait) is
    // met without it — iOS honours the app's supported orientations, and a
    // waiter is holding the phone upright anyway. Chrome under the Island on
    // every screen is not a fair price for enforcing that.
    //
    // The unconditional sweep stays and matters MORE than before: a device
    // previously enrolled as KDS (which locks landscape) carries that lock
    // into this station, and merely not setting a new one never clears one
    // already applied.
    useEffect(() => {
        setOrientation(null);
    }, []);

    // ---- shared identity assertion (unlock AND escape-revert use this) ---
    // Mirrors the web FunctionsPage employee-auth flow so the reused
    // TableServicePage (and its WaiterOrderDrawer, which reads username/
    // employeeId straight off useAuth()) always attributes orders to the
    // CURRENT operator. Both handleUnlock (below) and the escape-interception
    // effect route through this single mapping so it can't drift out of sync
    // between the two call sites.
    const assertOperatorIdentity = useCallback((op) => {
        setAuthenticatedState({
            isAuthenticated: true,
            userRole: op.role,
            username: op.name,
            tenantId: effectiveTenantId,
            employeeId: op.employeeId,
            authType: 'function',
        });
    }, [setAuthenticatedState, effectiveTenantId]);

    // ---- lock on logout (EOD, device-revoked/expired, etc.) -----------
    // useOperatorSession's `operator` is local hook state — it does NOT
    // react to AuthContext.logout(), so without this the shell would keep
    // showing the floor (still "unlocked") after AuthContext drops
    // authentication. Only fires while genuinely unlocked, so it never
    // calls lock() before anyone has signed in on first boot.
    useEffect(() => {
        if (!isAuthenticated && operator != null) lock();
    }, [isAuthenticated, operator, lock]);

    // ---- lock always exits sales-mode (Task W5) ------------------------
    // Sales-mode is purely in-memory (see header note) and must never
    // persist through a shift handover — a new operator unlocking the
    // station must always land back on Tables, never mid-cashier-switch.
    useEffect(() => {
        if (locked) {
            setSalesMode(false);
            setSalesPinOpen(false);
            setSalesOrdersOpen(false);
        }
    }, [locked]);

    // ---- unlock = shift handover --------------------------------------
    const handleUnlock = useCallback(async (pin) => {
        const next = await unlock(pin);
        if (next) {
            setShiftStartedAt(new Date());
            setActiveTab('tables');
            assertOperatorIdentity(next);
            if (next.preferredLanguage) changeLanguage(next.preferredLanguage);
        }
        return next;
    }, [unlock, assertOperatorIdentity, changeLanguage]);

    // Sales lives in the tab bar, not the top bar (fix/floor-plan-polish):
    // the bar is this station's one navigation surface. The tab is PIN-gated
    // — tapping it opens SalesPinModal; salesMode flips on success — and it
    // reads active while the cashier surface is showing. Legacy mode has no
    // cashier switch, so no Sales tab.
    const tabs = [
        { key: 'tables', label: t('tables'), icon: 'table' },
        ...(nativeTables ? [{ key: 'sales', label: t('sales'), icon: 'receipt' }] : []),
        { key: 'shift', label: t('shift'), icon: 'clock' },
    ];
    const activeTabKey = activeTab === 'tables' && salesMode ? 'sales' : activeTab;

    const handleTabChange = (key) => {
        if (key === 'sales') {
            if (salesMode) setActiveTab('tables');
            else setSalesPinOpen(true);
            return;
        }
        if (key === 'tables' && salesMode) {
            // What the old top-bar "Back to floor" chip did.
            setSalesMode(false);
            setSalesOrdersOpen(false);
        }
        setActiveTab(key);
    };

    const shiftSince = shiftStartedAt
        ? shiftStartedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        : null;

    return (
        <AuroraBackground intensity="calm">
            <style>{CSS}</style>
            {!nativeTables && (
                <LegacyNavigationGuard
                    lock={lock}
                    operator={operator}
                    assertOperatorIdentity={assertOperatorIdentity}
                />
            )}

            {everUnlocked ? (
                // W5 review fix (finding 2) — wraps the whole frame (inert
                // while salesMode is off, see header note); cashier's
                // TopBarExtras/SellSurface below are the only consumers.
                <SellExtrasProvider>
                <div className="wst-frame">
                    <StationTopBar
                        stationLabel={t('waiter')}
                        operator={operator || undefined}
                        onLock={lock}
                        onSettings={() => setSettingsOpen(true)}
                    >
                        {/* W5 review fix (finding 2) — session pill / connection
                            chip / More menu (Staff Allowance, printing +
                            translation toggles, End Session), same component
                            CashierStation mounts. Renders null internally until
                            SellSurface publishes a ready snapshot (only once
                            mounted below), same gating as the cashier surface. */}
                        {nativeTables && salesMode ? <CashierTopBarExtras /> : null}
                    </StationTopBar>
                    <ConnectionBanner />

                    <main className="wst-body">
                        {/* Tables — always mounted; display-toggled so an open table/order survives tab switches */}
                        <section
                            className="wst-pane"
                            role="tabpanel"
                            aria-label={t('tables')}
                            style={{ display: activeTab === 'tables' ? 'flex' : 'none' }}
                        >
                            <div className="wst-tables" style={glass({ borderRadius: 16 })}>
                                {/* Business-day gate stays ABOVE whichever surface is
                                    active — while closed, neither surface mounts. */}
                                {dayOpen ? (
                                    nativeTables ? (
                                        salesMode ? (
                                            // Task W5 cashier switch — the REUSED native
                                            // cashier surface, in place of TablesSurface. See
                                            // the header note: purely in-memory, station
                                            // config/operator identity untouched.
                                            // W5 review fix (finding 2) — full prop set,
                                            // mirroring CashierStation's own SellSurface
                                            // mount: ordersTabActive/onRequestOrdersTab/
                                            // onRequestSellTab (salesOrdersOpen stand-in for
                                            // the bottom Orders tab) + operatorUnlockedAt
                                            // (this station's own shiftStartedAt) so the
                                            // session-refresh-on-unlock effect inside
                                            // SellSurface works the same way it does for
                                            // CashierStation.
                                            <div className="wst-tables-native">
                                                <SellSurface
                                                    ordersTabActive={salesOrdersOpen}
                                                    onRequestOrdersTab={() => setSalesOrdersOpen(true)}
                                                    onRequestSellTab={() => setSalesOrdersOpen(false)}
                                                    operatorUnlockedAt={shiftStartedAt}
                                                    onLock={lock}
                                                />
                                            </div>
                                        ) : (
                                            <div className="wst-tables-native">
                                                {/* W4 fix — closes an in-progress order drawer on
                                                    lock; see TablesSurface's `locked` prop note. */}
                                                <TablesSurface
                                                    locked={locked}
                                                    salesPinOpen={salesPinOpen}
                                                    onCloseSalesPin={() => setSalesPinOpen(false)}
                                                />
                                            </div>
                                        )
                                    ) : (
                                        <div className="wst-tables-inner">
                                            <TableServicePage />
                                        </div>
                                    )
                                ) : (
                                    <div className="wst-tables-inner">
                                        <DayClosedScreen onRefresh={refreshDay} loading={dayLoading} />
                                    </div>
                                )}
                            </div>
                        </section>

                        {activeTab === 'shift' && (
                            <section className="wst-pane" role="tabpanel" aria-label={t('shift')}>
                                <div className="wst-shift">
                                    <div className="wst-op-card" style={glass({ borderRadius: 20 })}>
                                        <div className="wst-op-avatar" aria-hidden="true">{initials(operator?.name)}</div>
                                        <div className="wst-op-name">{operator?.name}</div>
                                        {operator?.role ? <div className="wst-op-role">{t(operator.role)}</div> : null}
                                        {shiftSince ? (
                                            <div className="wst-op-since">
                                                {ClockIcon}
                                                {t('on_shift_since')} {shiftSince}
                                            </div>
                                        ) : null}
                                        <button type="button" className="wst-end" onClick={lock}>
                                            {EndShiftIcon}
                                            {t('end_shift')}
                                        </button>
                                    </div>
                                </div>
                            </section>
                        )}
                    </main>

                    <BottomTabs tabs={tabs} activeKey={activeTabKey} onChange={handleTabChange} />
                </div>
                </SellExtrasProvider>
            ) : null}

            {locked && (
                <OperatorLockScreen
                    stationLabel={t('waiter')}
                    unlocking={unlocking}
                    error={error}
                    onSubmit={handleUnlock}
                    sessionStatus={sessionStatus}
                />
            )}

            <StationSettingsSheet open={settingsOpen} onClose={() => setSettingsOpen(false)} />

            {nativeTables ? (
                <SalesPinModal
                    open={salesPinOpen}
                    onClose={() => setSalesPinOpen(false)}
                    onSuccess={() => { setSalesPinOpen(false); setSalesMode(true); setActiveTab('tables'); }}
                    tenantId={effectiveTenantId}
                />
            ) : null}
        </AuroraBackground>
    );
}
